import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";
import { AuthContext } from "../../Context/AuthContext";

const sStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none", cursor: "pointer" };
const lStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 5, display: "block" };

const JoinSHG = () => {
  const [selectedVFC, setSelectedVFC] = useState("");
  const [selectedSHG, setSelectedSHG] = useState("");
  const [selectedSHGName, setSelectedSHGName] = useState("");
  const [members, setMembers] = useState([]);
  const [shgData, setShgData] = useState([]);
  const [newMembers, setNewMembers] = useState([]);
  const [designation, setDesignation] = useState("Member");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [vfcData, setVfcData] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [shgMembers, setShgMembers] = useState([]);
  const [alreadyAdded, setAlreadyAdded] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    if (!selectedJfmcType) return;
    const fetchVfcs = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const res = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId: selectedJfmcType._id }, config);
        setVfcData(res.data.vfcs || []);
      } catch (e) {
        console.error("Error fetching VFCs", e);
      } finally {
        setLoading(false);
      }
    };
    fetchVfcs();
    // reset on type change
    setSelectedVFC(""); setSelectedSHG(""); setMembers([]); setShgData([]); setShgMembers([]); setNewMembers([]);
  }, [selectedJfmcType]);

  const fetchSHGs = async (vfcId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const res = await axios.post(`${Api}/api/shgs/getShgsByVFC`, { vfcId }, config);
      setShgData((res.data.shgs || []).filter((s) => s && s.name));
    } catch (e) {
      setError("Failed to fetch SHGs.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (vfcId) => {
    setLoading(true);
    setMembers([]);
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/members/getMembersByVFC/${vfcId}`, config);
      setMembers(res.data.members || []);
    } catch (e) {
      setError("Failed to fetch members.");
    } finally {
      setLoading(false);
    }
  };

  const fetchShgMemberByShgId = async (id) => {
    try {
      const config = Authorization();
      const res = await axios.post(`${Api}/api/shgMember/members`, { shgId: id }, config);
      const valid = (res.data.members || []).filter((m) => m?.member?.name);
      setShgMembers(valid);
      setAlreadyAdded(valid);
    } catch (e) {
      console.error("Error fetching SHG members", e);
    }
  };

  const handleVfcSelect = (e) => {
    const vfcId = e.target.value;
    setSelectedVFC(vfcId);
    setSelectedSHG(""); setShgData([]); setShgMembers([]); setNewMembers([]);
    if (vfcId) { fetchMembers(vfcId); fetchSHGs(vfcId); }
  };

  const handleShgSelect = (e) => {
    const shgId = e.target.value;
    setSelectedSHG(shgId);
    setSelectedSHGName(e.target.options[e.target.selectedIndex]?.text || "");
    setNewMembers([]);
    if (shgId) fetchShgMemberByShgId(shgId);
  };

  const handleAddMember = () => {
    if (!selectedMember) return;
    const member = members.find((m) => m._id === selectedMember);
    if (!member) return;
    if (alreadyAdded.some((a) => a.member?._id === member._id) || newMembers.some((m) => m._id === member._id)) {
      setError("Member is already part of this SHG."); return;
    }
    setNewMembers((p) => [...p, member]);
    setError(null); setSelectedMember("");
  };

  const handleRemoveMember = (id) => setNewMembers((p) => p.filter((m) => m._id !== id));

  const handleSave = async () => {
    if (saving) return;
    if (!selectedSHG || newMembers.length === 0) { setError("Please select an SHG and add at least one member."); return; }
    setSaving(true);
    try {
      const config = Authorization();
      await axios.post(`${Api}/api/shgMember`, {
        shg: selectedSHG,
        member: newMembers.map((m) => m._id),
        designation,
      }, { headers: { "Content-Type": "application/json", ...config.headers } });
      setSuccess("Members saved successfully");
      setError(null);
      fetchShgMemberByShgId(selectedSHG);
      setNewMembers([]);
    } catch (e) {
      setError("Failed to save members.");
      setSuccess(null);
    } finally {
      setSaving(false);
    }
  };

  const availableMembers = members.filter((m) => !newMembers.some((n) => n._id === m._id));

  const selectFocus = (e) => (e.target.style.borderColor = "#4CAF50");
  const selectBlur = (e) => (e.target.style.borderColor = "#E2E8F0");

  if (!selectedJfmcType) {
    return (
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
        <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No JFMC Type Selected</div>
        <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
      </div>
    );
  }

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* Page Header */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Join SHG</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>SHG</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Add Members to SHG</span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        {/* Left: Form card */}
        <div style={{ flex: "0 0 60%", maxWidth: "60%" }}>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
              <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
                <i className="mdi mdi-account-multiple-plus" />
              </span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Add Members to SHG</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Select JFMC, SHG and members to add</div>
              </div>
            </div>

            <div style={{ padding: "20px 24px" }}>
              {error && (
                <div style={{ background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#C62828", marginBottom: 16 }}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: 16 }} />{error}
                </div>
              )}
              {success && (
                <div style={{ background: "#E8F5E9", border: "1px solid #A5D6A7", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#1B5E20", marginBottom: 16 }}>
                  <i className="mdi mdi-check-circle-outline" style={{ fontSize: 16 }} />{success}
                </div>
              )}

              {/* JFMC Select */}
              <div style={{ marginBottom: 14 }}>
                <label style={lStyle}><i className="mdi mdi-home-group" style={{ color: "#43A047", marginRight: 5 }} />JFMC</label>
                <select value={selectedVFC} onChange={handleVfcSelect} style={sStyle} onFocus={selectFocus} onBlur={selectBlur}>
                  <option value="">— Select JFMC —</option>
                  {vfcData.map((v) => <option key={v._id} value={v._id}>{v.vfcName}</option>)}
                </select>
              </div>

              {/* SHG Select */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ ...lStyle, color: !selectedVFC ? "#A0AEC0" : "#2D3748" }}>
                  <i className="mdi mdi-account-group" style={{ color: !selectedVFC ? "#CBD5E0" : "#43A047", marginRight: 5 }} />SHG
                </label>
                <select value={selectedSHG} onChange={handleShgSelect} disabled={!selectedVFC} style={{ ...sStyle, background: !selectedVFC ? "#F7FAFC" : "#fff", color: !selectedVFC ? "#A0AEC0" : "#1A202C", cursor: !selectedVFC ? "not-allowed" : "pointer" }} onFocus={selectFocus} onBlur={selectBlur}>
                  <option value="">— Select SHG —</option>
                  {shgData.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>

              {/* Designation */}
              <div style={{ marginBottom: 14 }}>
                <label style={lStyle}><i className="mdi mdi-badge-account-outline" style={{ color: "#43A047", marginRight: 5 }} />Designation</label>
                <select value={designation} onChange={(e) => setDesignation(e.target.value)} style={sStyle} onFocus={selectFocus} onBlur={selectBlur}>
                  <option value="Member">Member</option>
                  <option value="President">President</option>
                </select>
              </div>

              {/* Add Member row */}
              {selectedVFC && (
                <div style={{ marginBottom: 16 }}>
                  <label style={lStyle}><i className="mdi mdi-account-plus-outline" style={{ color: "#43A047", marginRight: 5 }} />Add Member</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <select value={selectedMember} onChange={(e) => setSelectedMember(e.target.value)} style={{ ...sStyle, flex: 1 }} onFocus={selectFocus} onBlur={selectBlur}>
                      <option value="">— Select Member —</option>
                      {availableMembers.map((m) => (
                        <option key={m._id} value={m._id} disabled={alreadyAdded.some((a) => a.member?._id === m._id)}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={handleAddMember} style={{ padding: "9px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                      <i className="mdi mdi-plus" /> Add
                    </button>
                  </div>
                </div>
              )}

              {/* Queued members list */}
              {newMembers.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#2D3748", marginBottom: 8 }}>Members to be added ({newMembers.length})</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {newMembers.map((m) => (
                      <div key={m._id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#F7FAF7", border: "1px solid #C8E6C9", borderRadius: 8, padding: "8px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg,#1B5E20,#43A047)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700 }}>
                            {m.name?.charAt(0).toUpperCase()}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1A202C" }}>{m.name}</span>
                          <span style={{ fontSize: 11, background: "#E8F5E9", color: "#1B5E20", padding: "1px 8px", borderRadius: 50, fontWeight: 600 }}>{designation}</span>
                        </div>
                        <button type="button" onClick={() => handleRemoveMember(m._id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#E53E3E", fontSize: 16 }}>
                          <i className="mdi mdi-close-circle-outline" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Save */}
              <button type="button" onClick={handleSave} disabled={saving} style={{ padding: "9px 22px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(46,125,50,0.3)", opacity: saving ? 0.7 : 1 }}>
                {saving ? <><i className="mdi mdi-loading mdi-spin" /> Saving…</> : <><i className="mdi mdi-content-save-outline" /> Save Members</>}
              </button>
            </div>
          </div>
        </div>

        {/* Right: Existing SHG Members */}
        <div style={{ flex: "0 0 40%", maxWidth: "40%" }}>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #EDF2F7", background: "#F7FAF7" }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-account-group" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>
                {selectedSHGName ? `${selectedSHGName} — Members` : "SHG Members"}
              </span>
              {shgMembers.length > 0 && (
                <span style={{ marginLeft: "auto", background: "#E8F5E9", color: "#1B5E20", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 50 }}>{shgMembers.length}</span>
              )}
            </div>
            <div style={{ padding: 16, maxHeight: 420, overflowY: "auto" }}>
              {shgMembers.length > 0 ? (
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                  {shgMembers.map((sm, i) => (
                    <li key={sm._id} style={{ background: "#F7FAF7", borderRadius: 8, padding: "9px 12px", border: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg,#1B5E20,#43A047)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {sm.member?.name?.charAt(0).toUpperCase()}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1A202C" }}>{sm.member?.name}</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 3 }}>
                          <span style={{ background: "#E8F5E9", color: "#1B5E20", padding: "1px 8px", borderRadius: 50, fontWeight: 600, fontSize: 11, border: "1px solid #A5D6A7" }}>{sm.designation}</span>
                          <span style={{ background: sm.member?.status === "A" ? "#E8F5E9" : "#FFF5F5", color: sm.member?.status === "A" ? "#1B5E20" : "#E53E3E", padding: "1px 8px", borderRadius: 50, fontWeight: 600, fontSize: 11 }}>
                            {sm.member?.status === "A" ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: "center", padding: "24px 0", color: "#A0AEC0", fontSize: 13 }}>
                  <i className="mdi mdi-account-clock-outline" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />
                  {selectedSHG ? "No members in this SHG yet" : "Select a SHG to view members"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinSHG;
